import { Injectable, OnInit } from '@angular/core';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { UserTask } from 'app/@types/dto/user-task.model';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { CheckAccessResult, DefaultFeature, Feature, UserRoleEnum, UiFeature } from './feature';
import { userRoles } from 'app/@types/dto/userRoles.model';
import { Select, Store } from '@ngxs/store';
import { UserAction, UserState, UserStatus } from 'app/main/state/user.state';
import { OrganizationAction, OrganizationState } from '@state/organization.state';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';

/**
 * Service computing the current UI Features, driven by current userTasks from userClaims.
 * These can change for a same user, when he selects an organization with different taskPlans
 */

@Injectable({
    providedIn: 'root',
})
export class FeatureService implements OnInit {
    /**
     * Observable to get notifications about feature changes
     */
    public feature$ = new BehaviorSubject<Feature>(DefaultFeature);
    @Select(UserState.getCurrentUserClaims) currentUserClaim$: Observable<UserClaims | undefined> | undefined;

    public UserReadiness: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(private store: Store, private readonly localStorageService: StorageService) {
        this.currentUserClaim$?.subscribe((claims) => this.handleUserClaims(claims));
    }
    ngOnInit(): void {}

    /**
     * Get the current full feature object
     */
    public getFeature(): Feature {
        return this.feature$.value;
    }

    public isOnboarded(): boolean {
        const organization = this.getOrganization();
        const currentUser = this.store.selectSnapshot(UserState.getCurrentUser);
        const currentUserStatus = this.store.selectSnapshot(UserState.getUserStatus);
        return (
            organization !== undefined &&
            organization.isOnboarded !== undefined &&
            organization.isOnboarded &&
            currentUser !== undefined &&
            (currentUserStatus === UserStatus.TERMS || currentUserStatus === UserStatus.ONBOARDED)
        );
    }

    /**
     * Check if the user has at least one of the requested uiFeatures, possibly enforcing an organization
     */
    public hasUiFeatures(uiFeatures: string[], forOrganization: boolean): boolean {
        const organization = this.getOrganization();
        if (forOrganization && organization === undefined) {
            return false;
        }
        if (uiFeatures.length === 0 || uiFeatures[0] === undefined) {
            return true;
        }
        const organizationId = forOrganization ? organization?.uid : undefined;
        const organizationEntityId = undefined;

        return uiFeatures.some((x) => this.feature$.value.hasUiFeature(x, organizationId, organizationEntityId));
    }

    /**
     * Check if the user has the sudo feature
     */
    public hasSudoFeature(): boolean {
        return this.feature$.value.hasSudoFeature();
    }

    /**
     * Get the current organization
     */
    public getOrganization(): OrganizationDto | undefined {
        return this.feature$.value.organization;
    }

    /**
     * Get the list of organization entities available for a given uiFeature
     */
    public getOrganizationEntities(uiFeatureName: string): OrganizationEntityDto[] {
        return this.feature$.value.getOrganizationEntities(uiFeatureName);
    }

    /**
     * Check access level for a uiFeature,
     *    possibly for an organization,
     *    further possibly for an organization entity
     * @param uiFeatureName
     * @param organizationId
     * @param organizationEntityId : specific-id, undefined=require-global, null=ignore
     * @returns 'none' | 'ro' | 'rw'
     */
    public checkAccess(
        uiFeature: string,
        organizationId: string | undefined,
        organizationEntityId: string | undefined | null,
    ): CheckAccessResult {
        return this.feature$.value.checkAccess(uiFeature, organizationId, organizationEntityId);
    }
    /**
     * Get serviceLevel of current organization
     * Throws if no organization is currently selected
     */
    public getUserRole(): UserRoleEnum | undefined {
        return this.feature$.value.getUserRole();
    }

    // Transform userClaims into Feature instance and notify
    public handleUserClaims(userClaims?: UserClaims) {
        if (!userClaims) {
            this.feature$.next(DefaultFeature);
            return;
        }

        // Constrain service_level into ServiceLevelEnum to get strong typing
        const userRole =
            userClaims.userRole && userClaims.userRole in userRoles ? <UserRoleEnum>userClaims.userRole : undefined;

        this.feature$.next(
            new Feature(
                userClaims.tasks.map((x) => this.createUiFeature(x)),
                userClaims.can_sudo || false,
                userClaims.is_sudo || false,
                userClaims.organization,
                userClaims.organization_entities,
                userRole,
            ),
        );
        this.UserReadiness.next(true);
    }

    // Create a UiFeature instance from a UserTask instance
    private createUiFeature(userTask: UserTask): UiFeature {
        // A UserTask name is 'name[:qualifier][:qualifier]'

        let qualifiers: string[] = [];
        let featureName: string;

        const tokens = userTask.name.split(':');

        if (tokens.length > 1) {
            featureName = tokens.shift()!;
            qualifiers = tokens;
        } else featureName = tokens[0] + '_admin';

        return new UiFeature(featureName, userTask.organization_id, userTask.organization_entity_id, qualifiers);
    }

    public impersonate(orgId: string): void {
        this.store.dispatch(new UserAction.Update(orgId));
        this.store.dispatch(new OrganizationAction.Activate(orgId));
        window.location.reload(); // Do not remove this
    }

    public reInitAdminSession(): void {
        this.store.dispatch(new UserAction.Update());
        this.store.dispatch(new OrganizationAction.Deactivate());
        this.localStorageService.removeItem(LocalStorageKeys.sudoMod);
    }
}
